// components/dashboard/widgets/AlertasRisco.jsx
import React from 'react';
import PropTypes from 'prop-types';
import { Spin, Empty, List, Tag, Avatar, Tooltip } from 'antd';
import { 
  ExclamationCircleOutlined, 
  WarningOutlined,
  InfoCircleOutlined,
  UserOutlined
} from '@ant-design/icons';
import { useDashboardData } from '../../../hooks/useDashboardData';

const AlertasRisco = ({ endpoint, filtros }) => {
  const { dados, loading, error } = useDashboardData(endpoint, filtros);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 40 }}>
        <Spin size="large" />
        <p style={{ marginTop: 16, color: '#666' }}>Carregando alertas...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ 
        padding: '20px', 
        textAlign: 'center', 
        backgroundColor: '#fff2f0',
        border: '1px solid #ffccc7',
        borderRadius: '6px',
        color: '#a8071a'
      }}>
        <ExclamationCircleOutlined style={{ fontSize: 24, marginBottom: 8 }} />
        <p style={{ margin: '0 0 4px 0', fontWeight: 'bold' }}>
          Erro ao carregar alertas
        </p>
        <small style={{ color: '#cf1322' }}>{error}</small>
      </div>
    );
  }

  if (!dados?.length) {
    return (
      <div style={{ 
        textAlign: 'center', 
        padding: 40, 
        color: '#52c41a'
      }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>🎉</div>
        <h4 style={{ color: '#52c41a', margin: 0 }}>
          Nenhum alerta crítico encontrado!
        </h4>
        <p style={{ color: '#8c8c8c', margin: '8px 0 0 0' }}>
          Todos os TCCs estão dentro dos parâmetros normais.
        </p>
      </div>
    );
  }

  const getPrioridade = (item) => {
    const { plagio_medio, ia_medio, nota_media, dias_medios } = item;
    
    if (plagio_medio > 70 || nota_media < 8) return 'CRÍTICO';
    if (plagio_medio > 50 || ia_medio > 60 || dias_medios > 60) return 'ALTO';
    if (plagio_medio > 30 || ia_medio > 40 || dias_medios > 30) return 'MÉDIO';
    return 'BAIXO';
  };

  const getTagColor = (prioridade) => {
    switch (prioridade) {
      case 'CRÍTICO': return 'red';
      case 'ALTO': return 'orange';
      case 'MÉDIO': return 'gold';
      case 'BAIXO': return 'green';
      default: return 'default';
    }
  };

  const getIcon = (prioridade) => {
    switch (prioridade) {
      case 'CRÍTICO': return <ExclamationCircleOutlined />;
      case 'ALTO': return <WarningOutlined />;
      default: return <InfoCircleOutlined />;
    }
  };

  const formatarProblemas = (item) => {
    const problemas = [];
    
    if (item.plagio_medio > 50) {
      problemas.push(`Plágio: ${item.plagio_medio}%`);
    }
    if (item.ia_medio > 50) {
      problemas.push(`IA: ${item.ia_medio}%`);
    }
    if (item.nota_media < 10) {
      problemas.push(`Nota baixa: ${item.nota_media}`);
    }
    if (item.dias_medios > 30) {
      problemas.push(`Atraso: ${item.dias_medios} dias`);
    }
    
    return problemas;
  };

  return (
    <div style={{ height: 350, overflowY: 'auto' }}>
      <List
        dataSource={dados.slice(0, 20)} // Mostrar top 20 alertas
        renderItem={(item, index) => {
          const prioridade = getPrioridade(item);
          const problemas = formatarProblemas(item);
          
          return (
            <List.Item style={{ 
              padding: '12px',
              borderBottom: '1px solid #f0f0f0',
              backgroundColor: prioridade === 'CRÍTICO' ? '#fff2f0' : 'transparent'
            }}>
              <List.Item.Meta
                avatar={
                  <Avatar 
                    icon={<UserOutlined />} 
                    style={{ 
                      backgroundColor: getTagColor(prioridade) === 'red' ? '#f5222d' : '#1890ff'
                    }}
                  />
                }
                title={
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontWeight: 'bold', fontSize: 13 }}>
                      {item.estudante || 'Estudante Anônimo'}
                    </span>
                    <Tag 
                      color={getTagColor(prioridade)}
                      icon={getIcon(prioridade)}
                      style={{ margin: 0, fontSize: 11 }}
                    >
                      {prioridade}
                    </Tag>
                  </div>
                }
                description={
                  <div style={{ fontSize: 12 }}>
                    {problemas.length > 0 ? (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                        {problemas.map((problema, idx) => (
                          <Tag 
                            key={idx} 
                            size="small" 
                            color={
                              problema.includes('Plágio') ? 'red' :
                              problema.includes('IA') ? 'orange' :
                              problema.includes('Nota') ? 'volcano' : 'blue'
                            }
                          >
                            {problema}
                          </Tag>
                        ))}
                      </div>
                    ) : (
                      <span style={{ color: '#8c8c8c' }}>
                        Dados de análise não disponíveis
                      </span>
                    )}
                    
                    {/* Informações adicionais */}
                    {(item.tipo_alerta || item.data_detectado) && (
                      <div style={{ marginTop: 4, fontSize: 11, color: '#999' }}>
                        {item.tipo_alerta && <span>Tipo: {item.tipo_alerta} </span>}
                        {item.data_detectado && <span>• Detectado: {new Date(item.data_detectado).toLocaleDateString()}</span>}
                      </div>
                    )}
                  </div>
                }
              />
              
              {item.estudante_id && (
                <Tooltip title="Ver detalhes do estudante">
                  <span style={{ 
                    color: '#1890ff', 
                    cursor: 'pointer',
                    fontSize: 11
                  }}>
                    ID: {item.estudante_id}
                  </span>
                </Tooltip>
              )}
            </List.Item>
          );
        }}
      />
      
      {/* Resumo no final */}
      {dados.length > 0 && (
        <div style={{ 
          padding: '12px', 
          backgroundColor: '#fafafa',
          borderTop: '1px solid #f0f0f0',
          textAlign: 'center',
          fontSize: 12,
          color: '#666'
        }}>
          <strong>{dados.length}</strong> alertas encontrados
          {dados.length > 20 && ` (mostrando os 20 primeiros)`}
        </div>
      )}
    </div>
  );
};

// PropTypes
AlertasRisco.displayName = 'AlertasRisco';
AlertasRisco.propTypes = {
  endpoint: PropTypes.string.isRequired,
  filtros: PropTypes.object
};

AlertasRisco.defaultProps = {
  filtros: {}
};

export default AlertasRisco;